import React, { useState, useEffect, useRef } from 'react';

interface Props {
  onEnterApp: () => void;
}

function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0)';
        } else {
          el.style.opacity = '0';
          el.style.transform = 'translateY(24px)';
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return ref;
}

function Reveal({ children, delay = 0, style = {} }: { children: React.ReactNode; delay?: number; style?: React.CSSProperties }) {
  const ref = useScrollReveal();
  return (
    <div ref={ref} style={{
      opacity: 0,
      transform: 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
}

export default function LandingPage({ onEnterApp }: Props) {
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      color: '#111827',
    }}>

      <style>{`
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@800&display=swap');
  @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }

  .hamburger-btn { display: none; }
.mobile-menu { display: none; }

@media (max-width: 768px) {
  .desktop-nav { display: none !important; }
  .hamburger-btn { display: flex; }
  .mobile-menu.open { display: flex; }
  .hero-title { font-size: 34px !important; }
  .hero-subtitle { font-size: 15px !important; }
  .stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
  .features-grid { grid-template-columns: 1fr !important; }
  .products-grid { grid-template-columns: 1fr !important; }
  .footer-grid { grid-template-columns: 1fr !important; gap: 28px !important; padding: 40px 24px 24px !important; }
  .footer-brand-col { text-align: center; margin-bottom: 4px; }
  .footer-brand-col > div:first-child { justify-content: center !important; }
  .footer-brand-col p { margin: 0 auto !important; text-align: center; }
  .footer-link-col { text-align: center; padding-bottom: 20px; border-bottom: 1px solid #1f2937; }
  .footer-link-col:last-child { border-bottom: none; padding-bottom: 0; }
  .footer-col-heading { font-size: 11px !important; margin-bottom: 12px !important; }
  .footer-links-list { gap: 10px !important; }
  .footer-links-list span { font-size: 14px !important; }
  .footer-bottom-bar { flex-direction: column; text-align: center; padding: 20px 24px !important; }
}
`}</style>

      {/* ── NAV ── */}
      <Reveal>
      <header style={{
        background: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        padding: '0 40px',
        height: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <img src="/logo-icon.png" alt="logo" style={{ width: 32, height: 32, borderRadius: 8, objectFit: 'cover', animation: 'float 3s ease-in-out infinite' }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <span style={{ fontWeight: 700, fontSize: '20px', color: '#111827', letterSpacing: '-0.01em' }}>
            CloudAIRambo
          </span>
        </div>
        <nav className="desktop-nav" style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
          {[
  { label: 'Products', action: () => document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' }) },
  { label: 'Features', action: () => document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' }) },
  { label: 'Pricing', action: onEnterApp },
].map(item => (
  <span key={item.label} onClick={item.action} style={{ fontSize: '14px', color: '#6b7280', textDecoration: 'none', fontWeight: 500, cursor: 'pointer' }}
    onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
    onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
  >{item.label}</span>
))}
          <a href="/team-login" style={{ fontSize: '14px', color: '#6b7280', textDecoration: 'none', fontWeight: 500 }}
            onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
            onMouseLeave={e => (e.currentTarget.style.color = '#6b7280')}
          >Team Login</a>
        </nav>
        <button
  className="hamburger-btn"
  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
  style={{
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    flexDirection: 'column',
    gap: '5px',
    padding: '8px',
  }}
>
  <span style={{ width: '22px', height: '2px', background: '#111827', display: 'block' }} />
  <span style={{ width: '22px', height: '2px', background: '#111827', display: 'block' }} />
  <span style={{ width: '22px', height: '2px', background: '#111827', display: 'block' }} />
</button>
      </header>
      <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`} style={{
  flexDirection: 'column',
  background: '#ffffff',
  borderBottom: '1px solid #e5e7eb',
  padding: '16px 24px',
  position: 'sticky',
  top: '60px',
  zIndex: 99,
  gap: '18px',
}}>
  <span onClick={() => { document.getElementById('products-section')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} style={{ fontSize: '15px', color: '#374151', fontWeight: 500, cursor: 'pointer' }}>Products</span>
  <span onClick={() => { document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }} style={{ fontSize: '15px', color: '#374151', fontWeight: 500, cursor: 'pointer' }}>Features</span>
  <span onClick={() => { onEnterApp(); setMobileMenuOpen(false); }} style={{ fontSize: '15px', color: '#374151', fontWeight: 500, cursor: 'pointer' }}>Pricing</span>
  <a href="/team-login" style={{ fontSize: '15px', color: '#374151', fontWeight: 500, textDecoration: 'none' }}>Team Login</a>
</div>
      </Reveal>

      {/* ── HERO ── */}
      <Reveal>
      <div style={{ textAlign: 'center', padding: '80px 24px 64px', borderBottom: '1px solid #e5e7eb', background: '#fafafa' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: '6px',
          background: '#f0f4ff', border: '1px solid #c7d4fe',
          borderRadius: '999px', padding: '5px 14px',
          fontSize: '12px', color: '#4f46e5', fontWeight: 600,
          marginBottom: '32px',
          animation: 'fadeUp 0.6s ease both',
        }}>
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6366f1', display: 'inline-block' }} />
          Enterprise Invoice Platform
        </div>

        <h1 className="hero-title" style={{
          fontSize: '56px', fontWeight: 800, color: '#111827',
          margin: '0 0 12px', lineHeight: 1.1, letterSpacing: '-0.02em',
          animation: 'fadeUp 0.6s 0.2s ease both', opacity: 0,
        }}>
          Invoice smarter.<br />
          <span style={{ background: 'linear-gradient(to right, #3b82f6, #9333ea)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Grow faster.
          </span>
        </h1>

        <p className="hero-subtitle" style={{ fontSize: '17px', color: '#6b7280', maxWidth: '500px', margin: '16px auto 0', lineHeight: 1.7, animation: 'fadeUp 0.6s 0.4s ease both', opacity: 0 }}>
          From solo shops to full teams — create invoices, run your POS,<br />
          manage clients and track every order. All in one place.
        </p>
      </div>
      </Reveal>

      {/* ── STATS ROW ── */}
      <div className="stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', borderBottom: '1px solid #e5e7eb', background: '#ffffff' }}>
        {[
          { value: '190+', label: 'Currencies' },
          { value: '15', label: 'Languages' },
          { value: '202', label: 'Invoice Templates' },
          { value: '99.9%', label: 'Uptime SLA' },
        ].map((stat, i) => (
          <Reveal key={stat.label} delay={i * 0.1} style={{ borderRight: i < 3 ? '1px solid #e5e7eb' : 'none' }}>
            <div style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: '36px', fontWeight: 800, color: '#3b82f6', marginBottom: '6px' }}>{stat.value}</div>
              <div style={{ fontSize: '13px', color: '#9ca3af', fontWeight: 500 }}>{stat.label}</div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* ── PLATFORM FEATURES ── */}
     <div id="features-section" style={{ maxWidth: '1280px', margin: '0 auto', padding: '72px 32px', width: '100%', boxSizing: 'border-box' }}>
        <Reveal>
          <div style={{ marginBottom: '48px' }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#6366f1', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px' }}>Platform Features</div>
            <h2 style={{ fontSize: '40px', fontWeight: 800, color: '#111827', margin: '0 0 12px', letterSpacing: '-0.02em' }}>
              Everything your business needs
            </h2>
            <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>
              A complete billing and operations platform built for modern teams.
            </p>
          </div>
        </Reveal>

        <div className="features-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1px', background: '#e5e7eb', border: '1px solid #e5e7eb', borderRadius: '16px', overflow: 'hidden' }}>
          {[
            { icon: '▦', color: '#3b82f6', bg: '#eff6ff', title: 'Invoice Builder', desc: 'Drag-and-drop canvas with 180+ industry templates. Quick, batch, and bilingual invoicing with pixel-perfect PDF export.' },
            { icon: '🌐', color: '#2563eb', bg: '#eff6ff', title: 'Bilingual Invoices, No Printer Needed', desc: 'Generate side-by-side bilingual invoices and send them straight to your client by email or WhatsApp — no printer required.' },
            { icon: '⊞', color: '#059669', bg: '#f0fdf4', title: 'POS & Live Inventory', desc: 'Full point-of-sale with barcode generation and scanning, receipt printing, and real-time stock levels as sales happen.' },
            { icon: '📦', color: '#0f766e', bg: '#f0fdfa', title: 'Suppliers & Stock Records', desc: 'Track suppliers, purchase records, and full stock history alongside your live inventory — not just current counts.' },
            { icon: '🏬', color: '#4338ca', bg: '#eef2ff', title: 'Multi-Outlet Management', desc: 'Run any number of outlets or branches under one account, each with its own inventory, staff, and settings.' },
            { icon: '🖨️', color: '#0d9488', bg: '#f0fdfa', title: 'Bluetooth Printing', desc: 'Connect a Bluetooth receipt printer for instant in-store printing when you do want a physical copy.' },
            { icon: '%', color: '#ea580c', bg: '#fff7ed', title: 'Custom Tax & Discount Rules', desc: 'Configure your own tax structure and discount rules per business, product, or client — built to match how you actually price.' },
            { icon: '⊡', color: '#db2777', bg: '#fdf2f8', title: 'Client Accounts & Auto-Discounts', desc: 'Save client profiles with full invoice history — set rules so returning or high-spend clients get discounts applied automatically.' },
            { icon: '📣', color: '#9333ea', bg: '#faf5ff', title: 'Self-Promotion Campaigns', desc: 'Turn your saved client data into targeted promotions and offers, sent directly from the platform.' },
            { icon: '◎', color: '#0891b2', bg: '#f0f9ff', title: 'Credit Book', desc: 'A running ledger for customer credit and dues — replace the notebook with a searchable digital record.' },
            { icon: '⌗', color: '#dc2626', bg: '#fef2f2', title: 'Advanced Queue Tracking', desc: 'Token-based queue management with live wait tracking your clients can follow in real time, plus display screens for the counter.' },
            { icon: '🍽️', color: '#b45309', bg: '#fffbeb', title: 'Advanced Order Tracking', desc: 'Live order tracking built for restaurants and dine-in — customers see order status in real time, kitchen to table.' },
            { icon: '⊙', color: '#7c3aed', bg: '#f5f3ff', title: 'Attendance & Payroll', desc: 'Face recognition and barcode-based attendance, feeding directly into leave tracking and payroll prep.' },
            { icon: '◉', color: '#9333ea', bg: '#faf5ff', title: 'Team IAM', desc: 'Role-based access control across every outlet — owner, manager, and staff permissions, managed from one place.' },
            { icon: '⊟', color: '#16a34a', bg: '#f0fdf4', title: 'Audit Logs', desc: 'Complete activity trail for compliance. Every action timestamped and attributed to a user.' },
          ].map((feature, i) => (
            <Reveal key={feature.title} delay={i * 0.08}>
              <div style={{ background: '#ffffff', padding: '32px 28px', transition: 'background 0.2s', height: '100%' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                onMouseLeave={e => (e.currentTarget.style.background = '#ffffff')}
              >
                <div style={{ width: '40px', height: '40px', background: feature.bg, border: `1px solid ${feature.color}22`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', color: feature.color, marginBottom: '20px' }}>{feature.icon}</div>
                <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '8px' }}>{feature.title}</div>
                <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.65 }}>{feature.desc}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

     

      {/* ── OUR PRODUCTS ── */}
      <div id="products-section" style={{ background: '#ffffff', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '72px 48px', width: '100%', boxSizing: 'border-box' }}>
          <Reveal>
            <div style={{ marginBottom: '40px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#6366f1', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '16px' }}>Our Products</div>
              <h2 style={{ fontSize: '40px', fontWeight: 800, color: '#111827', margin: '0 0 10px', letterSpacing: '-0.02em' }}>Choose your tool</h2>
              <p style={{ fontSize: '15px', color: '#6b7280', margin: 0 }}>Free utilities or the full enterprise platform — we have both.</p>
            </div>
          </Reveal>

          <div className="products-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <Reveal delay={0.1}>
              <div
                onClick={() => window.open('https://freetoolshub.cloudairambo.com', '_blank')}
                style={{ background: '#ffffff', border: `1px solid ${hoveredCard === 'pdf' ? '#3b82f6' : '#e5e7eb'}`, borderRadius: '16px', padding: '32px', cursor: 'pointer', transition: 'all 0.2s', position: 'relative', boxShadow: hoveredCard === 'pdf' ? '0 8px 24px rgba(59,130,246,0.1)' : '0 1px 4px rgba(0,0,0,0.04)' }}
                onMouseEnter={() => setHoveredCard('pdf')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{ position: 'absolute', top: '16px', right: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.08em' }}>FREE</div>
                <img src="/logo-icon.png" alt="logo" style={{ width: 48, height: 48, borderRadius: 12, objectFit: 'cover', marginBottom: 20 }} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <h3 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>CloudAI PDF</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.65, margin: '0 0 24px' }}>339 free file converter tools. Convert, compress, merge, split PDFs and documents instantly — no signup required.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['PDF Convert', 'Compress', 'Merge & Split', '+335 more'].map(tag => (
                    <span key={tag} style={{ background: '#eff6ff', color: '#2563eb', fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '999px', border: '1px solid #bfdbfe' }}>{tag}</span>
                  ))}
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.2}>
              <div
                onClick={onEnterApp}
                style={{ background: '#ffffff', border: `2px solid ${hoveredCard === 'rambo' ? '#9333ea' : '#e9d5ff'}`, borderRadius: '16px', padding: '32px', cursor: 'pointer', transition: 'all 0.2s', position: 'relative', boxShadow: hoveredCard === 'rambo' ? '0 8px 24px rgba(147,51,234,0.12)' : '0 1px 4px rgba(0,0,0,0.04)' }}
                onMouseEnter={() => setHoveredCard('rambo')}
                onMouseLeave={() => setHoveredCard(null)}
              >
                <div style={{ position: 'absolute', top: '16px', right: '16px', background: 'linear-gradient(to right, #3b82f6, #9333ea)', color: '#fff', fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '999px', letterSpacing: '0.08em' }}>FEATURED</div>
                <img
                  src="/logo-icon.png"
                  alt="logo"
                  style={{ width: 48, height: 48, objectFit: 'contain', marginBottom: 20 }}
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <h3 style={{ margin: '0 0 10px', fontSize: '20px', fontWeight: 700, color: '#111827', letterSpacing: '-0.01em' }}>CloudAI Invoice</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.65, margin: '0 0 24px' }}>Create and export invoices, run your POS, manage clients and ledger, track inventory, and handle your team — all in one platform.</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {['Invoice Builder', 'Team IAM', 'PDF Export', 'POS'].map(tag => (
                    <span key={tag} style={{ background: '#faf5ff', color: '#7c3aed', fontSize: '11px', fontWeight: 600, padding: '4px 10px', borderRadius: '999px', border: '1px solid #e9d5ff' }}>{tag}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

  {/* ── FOOTER (MATCHED EXACTLY TO FREETOOLSHUB COLORS & FONTS) ── */}
<Reveal>
  <footer style={{ background: '#f1f5f9', color: '#1f2937', marginTop: 'auto', borderTop: '1px solid #e2e8f0', fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}>
    <div className="footer-grid" style={{ 
      maxWidth: '1530px', 
      margin: '0 auto', 
      padding: '48px 24px 32px', 
      display: 'flex', 
      justifyContent: 'space-between', 
      flexWrap: 'wrap', 
      gap: '32px' 
    }}>
      {/* Brand Column */}
      <div className="footer-brand-col" style={{ maxWidth: '320px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <img 
            src="/logo-icon.png" 
            alt="CloudAIRambo Logo" 
            style={{ width: 40, height: 40, objectFit: 'contain' }} 
            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} 
          />
          <span style={{ fontWeight: 700, fontSize: '18px', color: '#111827' }}>
            CloudAIRambo
          </span>
        </div>
        <p style={{ fontSize: '14px', color: '#4b5563', lineHeight: 1.6, margin: '0 0 16px 0' }}>
          Built for real businesses. Invoice, POS, team management and operations — all in one platform.
        </p>

        {/* Real Social Links from FreeToolsHub */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <a href="https://x.com/CloudAiRambo" target="_blank" rel="noreferrer" title="Twitter / X" style={{ color: '#9ca3af', display: 'flex', transition: 'color 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.color = '#4b5563'} onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
            </svg>
          </a>
          <a href="https://linkedin.com/company/cloudairambo" target="_blank" rel="noreferrer" title="LinkedIn" style={{ color: '#9ca3af', display: 'flex', transition: 'color 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.color = '#4b5563'} onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
            </svg>
          </a>
          <a href="https://github.com/cloudairambo/CloudAiPDF" target="_blank" rel="noreferrer" title="GitHub" style={{ color: '#9ca3af', display: 'flex', transition: 'color 0.15s ease' }} onMouseEnter={e => e.currentTarget.style.color = '#4b5563'} onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path fillRule="evenodd" clipRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.53 1.032 1.53 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z"/>
            </svg>
          </a>
        </div>
      </div>

      {/* Link Columns with exact FreeToolsHub typography */}
      {[
        { heading: 'Products', links: [{ label: 'CloudAI PDF' }, { label: 'CloudAI Invoice' }] },
        { heading: 'App', links: [{ label: 'Team Login' }, { label: 'Pricing' }, { label: 'About' }, { label: 'Privacy Policy' }, { label: 'Terms of Service' }] },
        { heading: 'Features', links: [{ label: 'Invoice Builder' }, { label: 'POS System' }, { label: 'Team IAM' }] },
      ].map(col => (
        <div key={col.heading} className="footer-link-col" style={{ minWidth: '150px', textAlign: col.heading === 'Features' ? 'right' : 'left' }}>
          <h3 style={{ 
            fontSize: '14px', 
            fontWeight: 600, 
            color: '#111827', 
            textTransform: 'uppercase', 
            marginBottom: '14px' 
          }}>
            {col.heading}
          </h3>
          <div className="footer-links-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {col.links.map((link: any) => (
              <span
                key={link.label}
                onClick={() => {
                  if (link.label === 'Team Login') window.location.href = '/team-login';
                  if (link.label === 'About Us') window.location.href = '/about';
                  if (link.label === 'Privacy Policy') window.location.href = '/privacy';
                  if (link.label === 'Terms of Service') window.location.href = '/terms';
                  if (link.label === 'Pricing') onEnterApp();
                }}
                style={{
                  fontSize: '14px', 
                  color: '#4b5563', 
                  fontWeight: 400, 
                  cursor: ['Team Login', 'Pricing', 'About', 'Privacy Policy', 'Terms of Service'].includes(link.label) ? 'pointer' : 'default',
                  transition: 'color 0.15s ease',
                }}
                onMouseEnter={e => { if (['Pricing', 'Team Login', 'About', 'Privacy Policy', 'Terms of Service'].includes(link.label)) (e.currentTarget.style.color = '#111827'); }}
                onMouseLeave={e => (e.currentTarget.style.color = '#4b5563')}
              >
                {link.label}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Bottom Bar: border-t border-gray-200, text-xs text-gray-700 */}
    <div className="footer-bottom-bar" style={{ 
      borderTop: '1px solid #e2e8f0', 
      padding: '24px 24px', 
      maxWidth: '1530px',
      margin: '0 auto', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'space-between', 
      flexWrap: 'wrap', 
      gap: '12px' 
    }}>
      <span style={{ fontSize: '12px', color: '#374151' }}>
        © 2026 CloudAIRambo. All rights reserved.
      </span>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexShrink: 0 }}>
        <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />
        <span style={{ fontSize: '12px', color: '#374151', fontWeight: 500 }}>
          All systems operational
        </span>
      </div>
    </div>
  </footer>
</Reveal>
    </div>
  );
}
